<template>
  <div>
    <v-row>
      <v-col
          cols="6"
          sm="6"
          md="3"
          v-for="(availability, index) in firstRow"
          :key="'first-'+index"
      >
        <v-card
            rounded="lg"
            elevation="0"
            :class="isActiveAvailability(availability.id) ? 'selected' : 'not-selected'"
            class="pa-5 text-center cursor-pointer fw-700"
            @click="onAvailabilityChange(availability)"
        >
          {{ availability.name }}
        </v-card>
      </v-col>
    </v-row>
    <v-expansion-panels rounded="lg" color="surface" variant="accordion" class="mt-3">
      <v-expansion-panel eager class="w-100">
        <v-expansion-panel-title class="border-expansion">
          <template v-slot:default="{ expanded }">
            <div class="py-1">
              Altă perioadă
            </div>
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="background-default">
          <v-row class="pt-1">
            <v-col
                cols="6"
                sm="6"
                md="3"
                v-for="(availability, index) in secondRow"
                :key="'second-'+index"
            >
              <v-card
                  rounded="lg"
                  elevation="0"
                  :class="isActiveAvailability(availability.id) ? 'selected' : 'not-selected'"
                  class="pa-5 text-center cursor-pointer fw-700"
                  @click="onAvailabilityChange(availability)"
              >
                {{ availability.name }}
              </v-card>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-row v-show="error && error.length">
      <v-col cols="12">
        <v-alert type="error">
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import {defineComponent, ref, watch, onMounted, type PropType} from 'vue';
import {useRcaStore} from '~/plugins/store/rca';
import type {RcaAvailability} from "~/types/Rca";

export default defineComponent({
  name: 'RcaRadioAvailabilityListVuetify',
  props: {
    modelValue: {
      type: [Array, Number, null] as PropType<number[] | number | null>,
      default: () => [6, 12] as number[],
    },
  },
  setup(props, {emit}) {
    const rcaStore = useRcaStore();
    const selectedAvailability = ref<number[]>([6, 12]);
    const availabilityList = ref<RcaAvailability[]>([]);
    const error = ref('');

    const fetchAvailabilities = async () => {
      availabilityList.value = await rcaStore.getAvailabilities();
    };

    onMounted(() => {
      fetchAvailabilities();

      // Listen for validate event
      window.addEventListener('validate-rca', (data) => {
        if (data.detail && data.detail === 'availability') {
          if (!selectedAvailability.value) {
            error.value.push('Acest câmp este obligatoriu');
          } else {
            error.value = '';
            emit('validation-rca-status', {field: 'availability', isValid: true});
          }
        }
      });
    });

    const reversedAvailabilityList = computed(() => {
      return [...availabilityList.value].reverse();
    });

    // Împărțirea listelor de luni în două grupuri
    const firstRow = computed(() => {
      return reversedAvailabilityList.value.filter((availability) => [12, 6, 3, 1].includes(availability.id));
    });

    const secondRow = computed(() => {
      return reversedAvailabilityList.value.filter((availability) => [11, 10, 9, 8, 7, 5, 4, 2].includes(availability.id));
    });

    const isActiveAvailability = (availabilityId: number) => {
      if (Array.isArray(selectedAvailability.value)) {
        return selectedAvailability.value.includes(availabilityId);
      }
      return selectedAvailability.value === availabilityId;
    };

    // Logica pentru a selecta două luni la un moment dat
    const onAvailabilityChange = (availability: RcaAvailability) => {
      if (selectedAvailability.value.includes(availability.id)) {
        // Dacă luna este deja selectată, o scoatem din selecție
        selectedAvailability.value = selectedAvailability.value.filter(id => id !== availability.id);
      } else {
        // Dacă selecția are deja 2 luni, o eliminăm pe prima
        if (selectedAvailability.value.length >= 2) {
          selectedAvailability.value.shift(); // Scoatem prima lună selectată
        }
        // Adăugăm noua selecție
        selectedAvailability.value.push(availability.id);
      }

      emit('update:modelValue', [...selectedAvailability.value]); // Emiterea array-ului actualizat
    };

    watch(() => props.modelValue, (newValue) => {
      error.value = '';
      selectedAvailability.value = Array.isArray(newValue) ? newValue : [newValue];
      const isValid = !!newValue.length;
      emit('validation-rca-status', {field: 'availability', isValid});
    });

    return {
      firstRow,
      secondRow,
      selectedAvailability,
      reversedAvailabilityList,
      isActiveAvailability,
      onAvailabilityChange,
      error,
    };
  },
});
</script>

<style scoped>
.selected {
  border: 3px solid rgba(var(--v-theme-primary)); /* sau culoarea primară specifică */
  color: rgba(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.2); /* sau o variantă subtle a culorii primare */
}

.not-selected {
  border: 3px solid grey;
  color: grey;
  background-color: rgba(var(--v-theme-surface));
}
</style>
